import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';

import type { User } from '@/types/auth';
import { safeLocalStorage } from '@/lib/safe-storage';

interface ImpersonationState {
  isImpersonating: boolean;
  originalToken: string | null;
  originalRefreshToken: string | null;
  originalUser: User | null;
  originalTenantId: string | null;
  impersonatedTenantId: string | null;
  impersonatedTenantName: string | null;
}

/** A cleared impersonation state — used on login/logout/end to drop stale previews. */
const emptyImpersonation = (): ImpersonationState => ({
  isImpersonating: false,
  originalToken: null,
  originalRefreshToken: null,
  originalUser: null,
  originalTenantId: null,
  impersonatedTenantId: null,
  impersonatedTenantName: null,
});

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null;
  refreshTokenExpiresAt: number | null;
  tenantId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  lastActivity: number | null;
  sessionExpiry: number | null;
  hasCheckedAuth: boolean;

  // Impersonation state
  impersonation: ImpersonationState;

  // Actions
  setUser: (user: User) => void;
  setTokens: (
    token: string,
    refreshToken: string,
    tokenExpiresIn?: number,
    refreshTokenExpiresIn?: number
  ) => void;
  setLoading: (loading: boolean) => void;
  login: (
    user: User,
    token: string,
    refreshToken: string,
    tokenExpiresIn?: number,
    refreshTokenExpiresIn?: number
  ) => void;
  logout: () => void;
  updateLastActivity: () => void;
  setSessionExpiry: (expiry: number) => void;
  setHasCheckedAuth: (checked: boolean) => void;

  // Impersonation actions
  startImpersonation: (
    accessToken: string,
    refreshToken: string,
    impersonatedTenantId: string,
    impersonatedTenantName: string,
    expiresIn: number
  ) => void;
  endImpersonation: () => void;
  /** Drop any impersonation/preview state without restoring tokens (used on fresh login). */
  clearImpersonation: () => void;

  // Token management
  isTokenExpired: () => boolean;
  isRefreshTokenExpired: () => boolean;
  getValidToken: () => string | null;
  clearExpiredTokens: () => void;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  canAccess: (resource: string, action: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        token: null,
        refreshToken: null,
        tokenExpiresAt: null,
        refreshTokenExpiresAt: null,
        tenantId: null,
        isAuthenticated: false,
        isLoading: false,
        lastActivity: null,
        sessionExpiry: null,
        hasCheckedAuth: false,

        // Impersonation initial state
        impersonation: {
          isImpersonating: false,
          originalToken: null,
          originalRefreshToken: null,
          originalUser: null,
          originalTenantId: null,
          impersonatedTenantId: null,
          impersonatedTenantName: null,
        },

        // Actions
        setUser: (user) =>
          set({
            user,
            tenantId: user.tenantId,
            isAuthenticated: true,
            lastActivity: Date.now(),
          }),

        setTokens: (token, refreshToken, tokenExpiresIn = 7200, refreshTokenExpiresIn = 604800) => {
          const now = Date.now();

          // Defensive check to ensure tokenExpiresIn is a valid number
          const validTokenExpiry =
            typeof tokenExpiresIn === 'number' && !isNaN(tokenExpiresIn) ? tokenExpiresIn : 7200; // 2 hours default

          const validRefreshExpiry =
            typeof refreshTokenExpiresIn === 'number' && !isNaN(refreshTokenExpiresIn)
              ? refreshTokenExpiresIn
              : 604800; // 7 days default

          set({
            token,
            refreshToken,
            tokenExpiresAt: validTokenExpiry ? now + validTokenExpiry * 1000 : null,
            refreshTokenExpiresAt: validRefreshExpiry ? now + validRefreshExpiry * 1000 : null,
          });
        },

        setLoading: (loading) =>
          set({
            isLoading: loading,
          }),

        login: (
          user,
          token,
          refreshToken,
          tokenExpiresIn = 7200,
          refreshTokenExpiresIn = 604800
        ) => {
          const now = Date.now();
          set({
            user,
            token,
            refreshToken,
            tokenExpiresAt: now + tokenExpiresIn * 1000,
            refreshTokenExpiresAt: now + refreshTokenExpiresIn * 1000,
            tenantId: user.tenantId,
            isAuthenticated: true,
            isLoading: false,
            lastActivity: now,
            // A fresh login is never an impersonation — clear any stale state
            // (otherwise the persisted "preview/impersonation" banner sticks).
            impersonation: emptyImpersonation(),
          });
        },

        logout: () =>
          set({
            user: null,
            token: null,
            refreshToken: null,
            tokenExpiresAt: null,
            refreshTokenExpiresAt: null,
            tenantId: null,
            isAuthenticated: false,
            isLoading: false,
            lastActivity: null,
            sessionExpiry: null,
            hasCheckedAuth: false,
            impersonation: emptyImpersonation(),
          }),

        updateLastActivity: () =>
          set({
            lastActivity: Date.now(),
          }),

        setSessionExpiry: (expiry) =>
          set({
            sessionExpiry: expiry,
          }),

        setHasCheckedAuth: (checked) =>
          set({
            hasCheckedAuth: checked,
          }),

        // Impersonation actions
        startImpersonation: (
          accessToken,
          refreshTokenValue,
          impersonatedTenantId,
          impersonatedTenantName,
          expiresIn
        ) => {
          const state = get();
          const now = Date.now();

          set({
            // Save original tokens for restoration
            impersonation: {
              isImpersonating: true,
              originalToken: state.token,
              originalRefreshToken: state.refreshToken,
              originalUser: state.user,
              originalTenantId: state.tenantId,
              impersonatedTenantId,
              impersonatedTenantName,
            },
            // Switch to impersonation tokens
            token: accessToken,
            refreshToken: refreshTokenValue,
            tokenExpiresAt: now + expiresIn * 1000,
            tenantId: impersonatedTenantId,
            lastActivity: now,
          });
        },

        endImpersonation: () => {
          const { impersonation } = get();

          if (!impersonation.isImpersonating) return;

          set({
            // Restore original tokens
            token: impersonation.originalToken,
            refreshToken: impersonation.originalRefreshToken,
            user: impersonation.originalUser,
            tenantId: impersonation.originalTenantId,
            // Reset impersonation state
            impersonation: emptyImpersonation(),
            lastActivity: Date.now(),
          });
        },

        clearImpersonation: () => set({ impersonation: emptyImpersonation() }),

        // Token management
        isTokenExpired: () => {
          const { tokenExpiresAt } = get();
          if (!tokenExpiresAt) return true;
          return Date.now() > tokenExpiresAt;
        },

        isRefreshTokenExpired: () => {
          const { refreshTokenExpiresAt } = get();
          if (!refreshTokenExpiresAt) return true;
          return Date.now() > refreshTokenExpiresAt;
        },

        getValidToken: () => {
          const { token, isTokenExpired } = get();
          if (!token || isTokenExpired()) return null;
          return token;
        },

        clearExpiredTokens: () => {
          const { isTokenExpired, isRefreshTokenExpired } = get();
          if (isRefreshTokenExpired()) {
            // Both tokens expired, logout
            get().logout();
          } else if (isTokenExpired()) {
            // Only access token expired, clear it
            set({ token: null, tokenExpiresAt: null });
          }
        },

        hasPermission: (permission) => {
          const { user } = get();
          if (!user || !user.permissions) return false;

          // Check for superadmin wildcard permission
          if (user.permissions.includes('*')) return true;

          // Check specific permission
          return user.permissions.includes(permission);
        },

        hasRole: (role) => {
          const { user } = get();
          if (!user || !user.roles) return false;
          return user.roles.some((r) => r.toLowerCase() === role.toLowerCase());
        },

        hasAnyRole: (roles) => {
          const { user } = get();
          if (!user || !user.roles) return false;
          return user.roles.some((userRole) =>
            roles.some((role) => role.toLowerCase() === userRole.toLowerCase()),
          );
        },

        canAccess: (resource, action) => {
          const { user } = get();
          if (!user || !user.permissions) return false;

          // Check for superadmin wildcard permission
          if (user.permissions.includes('*')) return true;

          // Check resource wildcard
          if (user.permissions.includes(`${resource}:*`)) return true;

          // Check specific permission
          const permission = `${resource}:${action}`;
          return user.permissions.includes(permission);
        },
      }),
      {
        name: 'tms-auth-storage',
        // RULE 15: Use safe storage wrapper for mobile compatibility
        // Prevents exceptions in private/incognito mode and when storage is disabled
        storage: createJSONStorage(() => safeLocalStorage),
        // Persist auth data including tokens and impersonation state
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          refreshToken: state.refreshToken,
          tokenExpiresAt: state.tokenExpiresAt,
          refreshTokenExpiresAt: state.refreshTokenExpiresAt,
          tenantId: state.tenantId,
          isAuthenticated: state.isAuthenticated,
          lastActivity: state.lastActivity,
          impersonation: state.impersonation,
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Selectors
export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectToken = (state: AuthState) => state.token;
export const selectTenantId = (state: AuthState) => state.tenantId;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectLastActivity = (state: AuthState) => state.lastActivity;

// Helper hooks
export const useUser = () => useAuthStore(selectUser);
export const useIsAuthenticated = () => useAuthStore(selectIsAuthenticated);
export const useTenantId = () => useAuthStore(selectTenantId);
export const useAuthLoading = () => useAuthStore(selectIsLoading);

// Permission hooks
export const useHasPermission = (permission: string) => {
  return useAuthStore((state) => state.hasPermission(permission));
};

export const useHasRole = (role: string) => {
  return useAuthStore((state) => state.hasRole(role));
};

export const useCanAccess = (resource: string, action: string) => {
  return useAuthStore((state) => state.canAccess(resource, action));
};

// Impersonation selectors
export const selectImpersonation = (state: AuthState) => state.impersonation;
export const selectIsImpersonating = (state: AuthState) => state.impersonation.isImpersonating;
export const selectImpersonatedTenantName = (state: AuthState) =>
  state.impersonation.impersonatedTenantName;

// Impersonation hooks
export const useImpersonation = () => useAuthStore(selectImpersonation);
export const useIsImpersonating = () => useAuthStore(selectIsImpersonating);
export const useImpersonatedTenantName = () => useAuthStore(selectImpersonatedTenantName);
